import React, { useState, useEffect } from "react";
import "./LiveFindings.css";
import BarChart from "../../../BarChart";
import DatePicker from "react-datepicker";
import { Box, Typography, Paper, Grid, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNotesMedical,
  faListCheck,
  faArrowsRotate,
  faTriangleExclamation,
  faSackDollar,
  faClipboardList,
  faListOl,
  faCircleDot,
  faChartColumn,
  faShuffle
} from "@fortawesome/free-solid-svg-icons";
import { graphDataFunc, priorityOptionsFunc, statsFunc, roiCalculationFunc } from "./service";
import { formatCalenderDate } from "../web-workflow/utils/util";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LiveFindings = () => {
  const [liveFindingsFilter, setLiveFindingsFilter] = useState({
    start_date: null,
    end_date: null,
    episode_identifier: "",
  });
  const [statsData, setStatsData] = useState(null);
  const [findingsData, setFindingsData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [roiCalculationData, setRoiCalculationData] = useState(null);

  const [range, setRange] = useState("month");
  const [findingsView, setFindingsView] = useState("both");
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [graphError, setGraphError] = useState("");
  const [datepickerError, setDatepickerError] = useState(false);
  const [filterDataLoader, setFilterDataLoader] = useState(false);


  // ✅ safer default handling
  const totalConfirmedRevenue = graphData?.summary?.total_revenue ?? 0;
  const rev = Number(totalConfirmedRevenue);
  const roi = Number.isFinite(rev) ? Math.round((rev / 60000) * 100) : 0;

  // ✅ Dashboard cards
  const dashboardHeaderOptions = [
    {
      title: "Number of episodes processed",
      value:
        statsData?.episode_processed_stats?.number_of_episode_processed.toLocaleString() ?? "0",
      icon: faNotesMedical,
      color: "#1976d2",
    },
    {
      title: "Distinct episodes processed",
      value:
        statsData?.episode_processed_stats
          ?.number_of_distinct_episode_processed.toLocaleString() ?? "0",
      icon: faListCheck,
      color: "#2e7d32",
    },
    {
      title: "Episodes with DRG change",
      value: statsData?.episode_processed_stats?.episode_with_drg_change.toLocaleString() ?? "0",
      icon: faArrowsRotate,
      color: "#ed6c02",
    },
    {
      title: "DRG Error Rate",
      value:
        statsData?.episode_processed_stats?.number_of_episode_processed > 0
          ? `${(
              (statsData.episode_processed_stats.episode_with_drg_change /
                statsData.episode_processed_stats
                  .number_of_distinct_episode_processed) *
              100
            ).toFixed(2)}%`
          : "0%",
      icon: faTriangleExclamation,
      color: "#d32f2f",
    },
  ];

  // ✅ Fetch all API data initially
 useEffect(() => {
  setLoadingGraph(true);
  const payload = {
    start_date: formatCalenderDate(liveFindingsFilter.start_date),
    end_date: formatCalenderDate(liveFindingsFilter.end_date),
    episode_identifier: liveFindingsFilter.episode_identifier,
  };

  Promise.allSettled([
    statsFunc(payload),
    priorityOptionsFunc(payload),
    graphDataFunc({ ...payload, granularity: "month" }),
    roiCalculationFunc(payload)
  ]).then((results) => {
    const [statsRes, findingsRes, graphRes, roiCalculationRes] = results;

    console.log(roiCalculationRes, 'roiCalculationRes')

    if (statsRes.status === "fulfilled") {
      setStatsData(statsRes.value);
    } else {
      console.error("Stats API failed:", statsRes.reason);
    }

    if (findingsRes.status === "fulfilled") {
      setFindingsData(findingsRes.value);
    } else {
      console.error("Findings API failed:", findingsRes.reason);
    }

    if (graphRes.status === "fulfilled") {
      setGraphData(graphRes.value);
    } else {
      console.error("Graph API failed:", graphRes.reason);
      setGraphError("error");
    }

    if (roiCalculationRes.status === "fulfilled") {
      setRoiCalculationData(roiCalculationRes.value);
    } else {
      console.error("roiCalculation API failed:", roiCalculationRes.reason);
      setGraphError("error");
    }

    setLoadingGraph(false);
  });
}, []);


  // ✅ Show toast when stats data is empty
  useEffect(() => {
    // if (statsData) {
    //   const noStats =
    //     statsData?.confirmed_revenue === 0 &&
    //     statsData?.episode_processed_stats?.episode_with_drg_change === 0 &&
    //     statsData?.episode_processed_stats?.number_of_distinct_episode_processed === 0 &&
    //     statsData?.episode_processed_stats?.number_of_episode_processed === 0;

    //   if (noStats) {
    //     toast.error("No Data Available");
    //   }
    // }

    if (graphError) {
      toast.error(graphError);
    }
  }, [graphData, graphError]);

  const handleSubmit = async (e) => {
  e.preventDefault();

    setFilterDataLoader(true)
  const payload = {
    start_date: formatCalenderDate(liveFindingsFilter.start_date),
    end_date: formatCalenderDate(liveFindingsFilter.end_date),
    episode_identifier: liveFindingsFilter.episode_identifier,
  };

try {
    const [statsRes, findingsRes, graphRes, roiCalculationRes] = await Promise.allSettled([
      statsFunc(payload),
      priorityOptionsFunc(payload),
      graphDataFunc({ ...payload, granularity: range }),
      roiCalculationFunc(payload)
    ]);

    if (statsRes.status === "fulfilled") setStatsData(statsRes.value);
    if (findingsRes.status === "fulfilled") setFindingsData(findingsRes.value);
    if (graphRes.status === "fulfilled") setGraphData(graphRes.value);
    if (roiCalculationRes.status === "fulfilled") setRoiCalculationData(roiCalculationRes.value);
    else console.error("ROI calculation API failed:", graphRes.reason);
  } catch (err) {
    console.error("Error fetching filtered data:", err);
  } finally {
    setFilterDataLoader(false);
  }
};


const handleFromChange = (date) => {
  setLiveFindingsFilter((prev) => {
    const newFilter = { ...prev, start_date: date };

    if (newFilter.end_date && date && date > newFilter.end_date) {
      setDatepickerError("From date cannot be greater than To date");
    } else {
      setDatepickerError("");
    }

    return newFilter;
  });
};

// Handle To date change
const handleToChange = (date) => {
  setLiveFindingsFilter((prev) => {
    const newFilter = { ...prev, end_date: date };

    if (newFilter.start_date && date && newFilter.start_date > date) {
      setDatepickerError("Requested From date cannot be greater than Requested To date");
    } else {
      setDatepickerError("");
    }

    return newFilter;
  });
};


  const toggleFindingsView = () => {
    setFindingsView((prev) =>
      prev === "both" ? "distinct" : prev === "distinct" ? "total" : "both"
    );
  };

  return (
    <Box className="live-findings-main-container">
      <ToastContainer position="top-center" autoClose={2000} />

      <Box className="live-findings-content-container">
        {/* LEFT SECTION */}
        <Box className="live-findings-left-section">
    

          <Grid container spacing={2}>
            {dashboardHeaderOptions.map((option, index) => (
              <Grid item size={{ xs: 12, sm: 6 }} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    transition: "0.3s",
                    "&:hover": { boxShadow: 6 },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 500,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="sm"
                      style={{ marginRight: "8px", color: option.color }}
                    />
                    {option.title}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {option.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Chart Section */}
          <Paper elevation={3} sx={{ px: 2, py: 1, borderRadius: 3 }}>
            {/* <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Live API Dashboard
            </Typography> */}
            <BarChart
              range={range}
              setRange={setRange}
              graphData={graphData}
              setGraphData={setGraphData}
              liveFindingsFilter={liveFindingsFilter}
              loadingGraph={loadingGraph}
              setLoadingGraph={setLoadingGraph}
            />
          </Paper>
        </Box>

        {/* RIGHT SECTION */}
        <Paper
          className="live-findings-right-section"
          sx={{
            flex: 1,
            p: 0,
            borderRadius: 3,
            overflowY: "auto",
            maxHeight: "100vh",
            minWidth: "25%",
          }}
          elevation={3}
        >
          <Paper
            elevation={3}
            sx={{
              px: 3,
              py: 1,
              mb: 1,
              background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, display: "flex", alignItems: "center" }}
            >
              <FontAwesomeIcon
                icon={faSackDollar}
                size="sm"
                style={{ marginRight: "8px", color: "#2e7d32" }}
              />
              Total Confirmed Revenue
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              {/* ${totalConfirmedRevenue} */}
              {graphData
                ? `$${graphData?.summary?.total_revenue?.toLocaleString()}`
                : `$${0}`}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
              ROI: {roiCalculationData?.summary?.total_roi ? roiCalculationData?.summary?.total_roi : 0}%
            </Typography>
          </Paper>

          {/* Findings Section */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                         Findings </Typography>
             <Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "start",
    minWidth: "120px",
  }}
>
  {Object.keys(findingsData || {}).map((key, index) => (
 <Tooltip
  key={index}
  title={
    findingsView === "both"
      ? `Distinct: Episodes flagged only for this priority (${findingsData[key]?.distinct ?? 0}), 
         Total: Episodes flagged for this priority but may also be flagged under other rules (${findingsData[key]?.total ?? 0})`
      : findingsView === "distinct"
      ? `Distinct: Episodes flagged only for this priority (${findingsData[key]?.distinct ?? 0})`
      : `Total: Episodes flagged for this priority, but may also be flagged under other rules (${findingsData[key]?.total ?? 0})`
  }
  arrow
  placement="top"
>
      <Paper
        sx={{
          px: 2,
          py: 1,
          m: 1,
          borderRadius: "50px",
          backgroundColor: "#fff",
          fontSize: "0.8rem",
          fontWeight: 500,
          position: "relative",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
        elevation={3}
      >
        <span>
          P{key}:{" "}
          {findingsView === "both" && (
            <>
              {findingsData[key]?.distinct ?? 0} / {findingsData[key]?.total ?? 0}
            </>
          )}
          {findingsView === "distinct" && (
            <>{findingsData[key]?.distinct ?? 0}</>
          )}
          {findingsView === "total" && <>{findingsData[key]?.total ?? 0}</>}
        </span>

        {/* Toggle Icon */}
        <FontAwesomeIcon
          icon={
            findingsView === "both"
              ? faClipboardList // both
              : findingsView === "distinct"
              ? faCircleDot // distinct
              : faChartColumn // total
          }
          size="sm"
          style={{
            marginLeft: "6px",
            fontSize: "14px",
            color: "#4897c6",
            cursor: "pointer",
          }}
          onClick={toggleFindingsView}
        />
      </Paper>
    </Tooltip>
  ))}
</Box>

            {/* Filters Section */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
              Filters
            </Typography>
            <form
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
              onSubmit={handleSubmit}
            >
              {/* Requested From */}
              <div>
                <label
                  className="live-findings-form-filters-container-label"
                  htmlFor="from"
                >
                  Requested From
                </label>
                <DatePicker
                  id="from"
                  selected={liveFindingsFilter.start_date}
                  placeholderText="Requested From"
                  className="live-findings-form-filters-container-date-picker"
                  onChange={handleFromChange}
                  maxDate={new Date()}
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                  dateFormat="dd/MM/yyyy"
                  autoComplete="off"
                />
              </div>

              {/* Requested To */}
              <div>
                <label
                  className="live-findings-form-filters-container-label"
                  htmlFor="to"
                >
                  Requested To
                </label>
                <DatePicker
                  id="to"
                  selected={liveFindingsFilter.end_date}
                  placeholderText="Requested To"
                  className="live-findings-form-filters-container-date-picker"
                  onChange={handleToChange}
                  maxDate={new Date()}
                  isClearable
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                  dateFormat="dd/MM/yyyy"
                  autoComplete="off"
                />
              </div>
              {datepickerError && (
  <div style={{ color: "red", marginTop: "5px", fontSize: "12px" }}>
    {datepickerError}
  </div>
)}
              {/* Episode Identifier */}
              <div>
                <label
                  className="live-findings-form-filters-container-label"
                  htmlFor="episode_ID"
                >
                  Episode Identifier
                </label>
                <input
                  id="episode_ID"
                  className="live-findings-form-filters-container-inputFiled"
                  type="text"
                  placeholder="Search by Episode ID"
                  value={liveFindingsFilter.episode_identifier}
                  onChange={(e) =>
                    setLiveFindingsFilter((prev) => ({
                      ...prev,
                      episode_identifier: e.target.value,
                    }))
                  }
                  autoComplete="off"
                />
              </div>

              <div>
  <button
    type="submit"
    className="live-findings-form-filters-container-button"
    disabled={!!datepickerError || filterDataLoader}
  >
    {filterDataLoader ? "Loading..." : "Apply Filters"}
  </button>
</div>

            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LiveFindings;
